import { Box, Button, Modal, SpaceBetween, Table } from '@amzn/awsui-components-console';
import React, { useCallback, useState } from 'react';
import { CustomStatusIndicator, ToggleableDatetime } from '../helpers';
import InstanceOverview from 'src/interfaces/instanceInterfaces';
import keys from '../../constants/hammerstoneConstantKeys';
import store, { actions, useGroupName } from 'src/data/redux';
import { PutInstanceStatus } from 'src/lib/hammerstoneApi';
import { headerCounter } from 'src/commons/componentHelpers';
import { durationToString, useConditionalEffect } from 'src/commons';
import PAGES from '../../nav/Pages';
import { fetchInstanceById } from 'src/data/api/fetchFromAPI';
import { Operation } from 'src/constants/hammerstoneConstants';
import InternalLink from '../helpers/InternalLink';

export default function InstanceBulkActionConfirmModal(props: {
	action: string;
	setAction: (action: string) => void;
	selectedInstances: InstanceOverview[];
}) {
	const groupName = useGroupName();
	const operationValue = props.action ? Operation[props.action as keyof typeof keys.Operation] : '';
	const [isVisible, setIsVisible] = useState(false);

	useConditionalEffect(() => {
		setIsVisible(true);
	}, [props.action]);

	const executeBulkAction = useCallback(async () => {
		let completedCount = 0;

		try {
			for (const instance of props.selectedInstances) {
				try {
					const activityId = instance.activityId;
					const { instanceId } = await PutInstanceStatus({
						instanceId: instance.instanceId,
						activityId: activityId,
						groupName: groupName,
						body: { operation: props.action as keyof typeof keys.Operation },
					});

					await fetchInstanceById({ instanceId, activityId: activityId, groupName }, true, false);

					completedCount++;

					store.dispatch(
						actions.page.addToFlashbar({
							id: `bulk-instance-operation-progress-${activityId}`,
							message: {
								type: 'info',
								header: `Performing ${operationValue.toLowerCase()}...`,
								content: `Completed ${headerCounter(
									completedCount,
									props.selectedInstances.length
								)} instances.`,
								dismissible: true,
							},
						})
					);
				} catch (error) {
					store.dispatch(
						actions.page.addToFlashbar({
							id: `bulk-instance-operation-error-${instance.instanceId}`,
							message: {
								type: 'error',
								header: `Failed to perform ${operationValue.toLowerCase()}`,
								content: `Failed for instance ${instance.instanceId}: ${
									error?.message || 'Unknown error'
								}`,
								dismissible: true,
							},
						})
					);
				}
			}
		} finally {
			store.dispatch(
				actions.page.addToFlashbar({
					id: `bulk-instance-operation-complete`,
					message: {
						type: 'success',
						header: `${operationValue} completed`,
						content: `Completed ${operationValue.toLowerCase()} operation for ${headerCounter(
							completedCount,
							props.selectedInstances.length
						)} instances.`,
						dismissible: true,
					},
				})
			);
			props.setAction('');
		}
	}, [props.selectedInstances, props.action, groupName, props.setAction, operationValue]);

	const handleModalDismiss = () => {
		setIsVisible(false);
		props.setAction('');
	};

	const handleSubmit = () => {
		setIsVisible(false);
		executeBulkAction();
	};

	return (
		<Modal
			onDismiss={handleModalDismiss}
			visible={isVisible}
			closeAriaLabel="Close action modal"
			size="max"
			footer={
				<Box float="right">
					<SpaceBetween size="xs" direction="horizontal">
						<Button
							variant="primary"
							iconName="upload"
							data-testid="submit-upload-confidence-file-button"
							onClick={handleSubmit}
						>
							Submit
						</Button>
					</SpaceBetween>
				</Box>
			}
			header={`Bulk ${operationValue.toLowerCase()}`}
		>
			<SpaceBetween size="m">
				<div>
					Operation <b>{operationValue.toLowerCase()}</b> will be performed for the following instances,
					please review your selection and confirm:
				</div>
				<Table
					data-testid="upload-confidence-file-instances-table"
					items={props.selectedInstances}
					columnDefinitions={[
						{
							id: 'id',
							header: 'ID',
							cell: ({ activityId, instanceId }) => (
								<InternalLink
									href={PAGES.VIEW_INSTANCE.path}
									pathParams={{ activityId, instanceId }}
									groupName
								>
									{instanceId}
								</InternalLink>
							),
						},
						{
							id: 'status',
							header: 'Status',
							cell: (item) => <CustomStatusIndicator>{item.status}</CustomStatusIndicator>,
						},
						{
							id: 'schedule',
							header: 'Schedule',
							cell: (item) => <ToggleableDatetime>{item.scheduleDateTime}</ToggleableDatetime>,
						},
						{
							id: 'duration',
							header: 'Duration',
							cell: (item) => durationToString(item.duration, { granularity: 2 }),
							sortingField: 'duration',
						},
						{
							id: 'start',
							header: 'Start',
							cell: (item) => <ToggleableDatetime>{item.startDateTime}</ToggleableDatetime>,
						},
						{
							id: 'end',
							header: 'End',
							cell: (item) => <ToggleableDatetime>{item.endDateTime}</ToggleableDatetime>,
						},
					]}
				/>
			</SpaceBetween>
		</Modal>
	);
}
